import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { Option } from 'src/components/SelectBox';
import { Textarea } from 'src/components/Textarea';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { v4 as uuidv4 } from 'uuid';
import type { CampaignInit } from '../campaignWizard.types';
import { ScheduleSelector } from './ScheduleSelector';

type CreateCampaignDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onNext?: (data: {
    name: string;
    description: string;
    contactWindow: string;
  }) => void;
};

export const CreateCampaignDialog = ({
  isOpen,
  onClose,
  onNext,
}: CreateCampaignDialogProps) => {
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedContactWindowOption, setSelectedContactWindowOption] =
    useState<Option>({
      label: 'Select campaign schedule',
      value: '',
    });
  const [contactWindowOptions, setContactWindowOptions] = useState<Option[]>(
    [],
  );
  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [isLoadingContactWindows, setIsLoadingContactWindows] = useState(false);

  const [selectedContactWindow, setSelectedContactWindow] =
    useState<WorkflowContactWindow | null>(null);

  const [searchParams] = useSearchParams();
  const campaignName = searchParams.get('campaignName');
  const campaignDescription = searchParams.get('campaignDescription');
  const campaignSchedule = searchParams.get('campaignSchedule');

  const getCampaignsReturnToUrl = () => {
    const params = new URLSearchParams({
      createCampaign: '1',
      campaignName: name,
      campaignDescription: description,
    });

    const nextCampaignSchedule =
      selectedContactWindowOption.value || campaignSchedule;
    if (nextCampaignSchedule) {
      params.set('campaignSchedule', String(nextCampaignSchedule));
    }

    return `/beta/campaigns?${params.toString()}`;
  };

  const navigateToCampaignScheduleEditor = (contactWindowId?: string) => {
    const params = new URLSearchParams({
      returnTo: getCampaignsReturnToUrl(),
    });
    if (contactWindowId) {
      params.set('id', contactWindowId);
    }

    navigate(`/beta/campaigns/contact-window?${params.toString()}`);
  };

  // Populate form fields from URL params when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (campaignName) {
        setName(campaignName);
      }
      if (campaignDescription) {
        setDescription(campaignDescription);
      }
    }
  }, [isOpen, campaignName, campaignDescription]);

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedContactWindowOption({
      label: 'Select campaign schedule',
      value: '',
    });
    setSelectedContactWindow(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      setIsLoadingContactWindows(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
        const optionItems = records.map((record) => ({
          label: record.label,
          value: record.id,
        }));
        setContactWindowOptions(optionItems);
        const selectedRecord =
          records.find((record) => record.id === campaignSchedule) ||
          (records.length > 0 ? (records[0] as WorkflowContactWindow) : null);
        if (selectedRecord) {
          setSelectedContactWindowOption({
            label: selectedRecord.label,
            value: selectedRecord.id,
          });
        } else {
          setSelectedContactWindowOption({
            label: 'Select campaign schedule',
            value: '',
          });
        }
        setSelectedContactWindow(selectedRecord || null);
      } catch {
        setContactWindows([]);
        setContactWindowOptions([]);
        setSelectedContactWindowOption({
          label: 'Select campaign schedule',
          value: '',
        });
        setSelectedContactWindow(null);
      } finally {
        setIsLoadingContactWindows(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const normalizeReferenceId = (value: string) =>
    value
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');

  const nextDisabled =
    !name ||
    isLoadingContactWindows ||
    contactWindowOptions.length === 0 ||
    !selectedContactWindowOption.value;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create campaign"
      actionButton={
        <Button
          onClick={() => {
            onNext?.({
              name,
              description,
              contactWindow: String(selectedContactWindowOption.value),
            });
            if (name) {
              const normalized = normalizeReferenceId(name);
              const referenceId = `${normalized}-${uuidv4()}`;
              const campaignInit: CampaignInit = {
                name,
                description,
                referenceId,
                contactWindowId: String(selectedContactWindowOption.value),
              };
              navigate('/beta/campaigns/new', {
                state: {
                  campaignInit,
                },
              });
            }
          }}
          disabled={nextDisabled}
        >
          Next
        </Button>
      }
      className="lg:max-w-[550px]"
    >
      <div className="space-y-4 pr-2">
        <div>
          <Label>Campaign name *</Label>
          <Input
            placeholder="Enter campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign description</Label>
          <Textarea
            placeholder="Enter campaign description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign schedule</Label>
          <ScheduleSelector
            contactWindows={contactWindows}
            selectedContactWindow={selectedContactWindow}
            onContactWindowChange={(cw) => {
              if (cw) {
                setSelectedContactWindowOption({
                  label: cw.label,
                  value: cw.id,
                });
                setSelectedContactWindow(cw);
              } else {
                setSelectedContactWindowOption({
                  label: 'Select campaign schedule',
                  value: '',
                });
                setSelectedContactWindow(null);
              }
            }}
            isLoading={isLoadingContactWindows}
            onNavigateToScheduleEditor={navigateToCampaignScheduleEditor}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreateCampaignDialog;
